import React, { useEffect } from 'react';
import { 
  Autocomplete,
  Box, 
  Card, 
  CardContent, 
  Typography, 
  TextField,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LanguageFlag from '../common/LanguageFlag';
import LanguageSelector from './LanguageSelector';
import MultiLanguageTextArea from './MultiLanguageTextArea';
import { getCurrencyOptions, DEFAULT_CURRENCY } from '../../utils/currencyUtils';
import { getTimeZoneOptions, getPopularTimeZoneOptions, getDefaultTimeZone, getTimeZoneDisplayLabel } from '../../utils/timeZoneUtils';
import { SUPPORTED_LANGUAGE_CODES } from '../../constants/Languages';
import StoreSlugField from '../stores/StoreSlugField';
import { slugifyStoreName } from '../../utils/storeSlugUtils';
import { BRAND } from '../../theme/brandTokens';

const StoreBasicInfo = ({
  formData,
  updateFormData,
  errors,
  setErrors,
  mode = 'admin',
  syncSlugFromName = false,
  originalSlug,
}) => {
  const { t } = useTranslation();
  const isSelfService = mode === 'self-service';

  const currencyOptions = getCurrencyOptions();
  const defaultTimeZone = getDefaultTimeZone();
  const timeZoneOptions = getTimeZoneOptions();
  const popularTimeZoneOptions = getPopularTimeZoneOptions();
  const allTimeZoneOptions = timeZoneOptions.filter((timeZone) => !popularTimeZoneOptions.includes(timeZone));
  const groupedTimeZoneOptions = [
    ...popularTimeZoneOptions.map((timeZone) => ({ value: timeZone, group: t('storeRegistration.basicInfo.topTimeZones') })),
    ...allTimeZoneOptions.map((timeZone) => ({ value: timeZone, group: t('storeRegistration.basicInfo.allTimeZones') }))
  ];
  const selectedTimeZone = formData.timeZone || defaultTimeZone;

  const languageOptions = SUPPORTED_LANGUAGE_CODES.map((code) => ({
    code,
    name: t(`common.languages.${code}`),
  }));

  const availableLanguages = SUPPORTED_LANGUAGE_CODES.map((code) => ({
    code,
    name: t(`common.languages.${code}`),
  }));
  const storeLanguageOptions = availableLanguages.filter((language) =>
    formData.supportedLanguages.includes(language.code)
  );
  const shouldShowStoreLanguageField = storeLanguageOptions.length > 1;
  const formatFieldLabel = (label, required = false) => (required ? `${label} *` : label);

  const getFieldSx = (hasError) => ({
    '& .MuiOutlinedInput-root': {
      height: '48px',
      minHeight: '48px',
      borderRadius: '8px',
      backgroundColor: '#fff',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: hasError ? '#f44336' : BRAND.line,
        borderWidth: '2px'
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: hasError ? '#f44336' : BRAND.accent
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: hasError ? '#f44336' : BRAND.accent,
        borderWidth: '2px'
      }
    },
    '& .MuiOutlinedInput-input': {
      padding: '12px 16px',
      fontSize: '16px'
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      paddingTop: '0 !important',
      paddingBottom: '0 !important',
      paddingLeft: '16px !important',
      alignItems: 'center'
    },
    '& .MuiAutocomplete-input': {
      padding: '0 !important',
      fontSize: '16px'
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0,
      marginRight: 0
    }
  });

  const getSelectSx = (hasError) => ({
    minHeight: '48px !important',
    borderRadius: '8px',
    backgroundColor: '#fff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: `${hasError ? '#f44336' : BRAND.line} !important`,
      borderWidth: '2px !important',
      borderRadius: '8px !important'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: `${hasError ? '#f44336' : BRAND.accent} !important`
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: `${hasError ? '#f44336' : BRAND.accent} !important`,
      borderWidth: '2px !important'
    },
    '& .MuiSelect-select': {
      padding: '12px 16px !important',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center'
    }
  });

  const handleLanguageChange = (languageCode, checked) => {
    const newSupportedLanguages = checked
      ? [...formData.supportedLanguages, languageCode]
      : formData.supportedLanguages.filter(lang => lang !== languageCode);

    const nextStoreLanguage = newSupportedLanguages.includes(formData.storeLanguage)
      ? formData.storeLanguage
      : (newSupportedLanguages[0] || '');

    updateFormData({ supportedLanguages: newSupportedLanguages, storeLanguage: nextStoreLanguage });

    // Clear errors when languages change
    if (errors.languages) {
      setErrors(prev => ({ ...prev, languages: undefined }));
    }
    if (errors.storeLanguage && nextStoreLanguage) {
      setErrors(prev => ({ ...prev, storeLanguage: undefined }));
    }
  };

  const handleAboutChange = (about) => {
    updateFormData({ about });

    // Clear errors when about content changes
    if (errors.about) {
      setErrors(prev => ({ ...prev, about: undefined }));
    }
  };

  useEffect(() => {
    if (!formData.timeZone && defaultTimeZone) {
      updateFormData({ timeZone: defaultTimeZone });
      if (errors.timeZone) {
        setErrors((prev) => ({ ...prev, timeZone: undefined }));
      }
    }
  }, [defaultTimeZone, errors.timeZone, formData.timeZone, setErrors, updateFormData]);

  useEffect(() => {
    const hasSupportedLanguages = formData.supportedLanguages.length > 0;
    const hasValidStoreLanguage = hasSupportedLanguages && formData.supportedLanguages.includes(formData.storeLanguage);

    if (!hasSupportedLanguages || hasValidStoreLanguage) {
      return;
    }

    updateFormData({ storeLanguage: formData.supportedLanguages[0] });

    if (errors.storeLanguage) {
      setErrors((prev) => ({ ...prev, storeLanguage: undefined }));
    }
  }, [errors.storeLanguage, formData.storeLanguage, formData.supportedLanguages, setErrors, updateFormData]);

  useEffect(() => {
    if (shouldShowStoreLanguageField || !errors.storeLanguage) {
      return;
    }

    setErrors((prev) => ({ ...prev, storeLanguage: undefined }));
  }, [errors.storeLanguage, setErrors, shouldShowStoreLanguageField]);

  return (
    <Box id="basic-info-step" sx={{ mt: 2 }}>

      <Typography variant="h5" sx={{ mb: 1, color: BRAND.text, fontWeight: 600 }}>
        {t('storeRegistrationSteps.step1.title')}
      </Typography>
      <Typography variant="body1" sx={{ mb: 4, color: BRAND.textMuted }}>
        {t('storeRegistrationSteps.step1.description')}
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {formatFieldLabel(t('storeRegistration.basicInfo.storeName'), true)}
              </Typography>
              <TextField
                fullWidth
                placeholder={t('storeRegistration.basicInfo.storeNameLabel')}
                value={formData.name}
                onChange={(e) => {
                  const name = e.target.value;
                  const updates = { name };
                  if (syncSlugFromName && !formData.slugManuallyEdited) {
                    updates.slug = slugifyStoreName(name);
                  }
                  updateFormData(updates);
                  if (errors.name) {
                    setErrors(prev => ({ ...prev, name: undefined }));
                  }
                }}
                error={!!errors.name}
                helperText={errors.name}
                sx={getFieldSx(!!errors.name)}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {t('storeRegistration.basicInfo.currency')}
              </Typography>

              <FormControl fullWidth>
                <Select
                  value={formData.currency || DEFAULT_CURRENCY}
                  onChange={(e) => {
                    updateFormData({ currency: e.target.value });
                    if (errors.currency) {
                      setErrors(prev => ({ ...prev, currency: undefined }));
                    }
                  }}
                  error={!!errors.currency}
                  MenuProps={{ disableScrollLock: true }}
                  sx={getSelectSx(!!errors.currency)}
                >
                  {currencyOptions.map((currency) => (
                    <MenuItem key={currency.code} value={currency.code}>
                      {currency.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {errors.currency && (
                <Alert severity="error" sx={{ mt: 1.5 }}>{errors.currency}</Alert>
              )}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <StoreSlugField
                value={formData.slug}
                onChange={(slug) => updateFormData({ slug, slugManuallyEdited: true })}
                error={errors.slug}
                setErrors={setErrors}
                currentSlug={originalSlug}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {formatFieldLabel(t('storeRegistration.basicInfo.timeZone'), !isSelfService)}
              </Typography>

              <Autocomplete
                fullWidth
                options={groupedTimeZoneOptions}
                value={groupedTimeZoneOptions.find((option) => option.value === selectedTimeZone) || { value: selectedTimeZone, group: t('storeRegistration.basicInfo.allTimeZones') }}
                onChange={(_, option) => {
                  updateFormData({ timeZone: option?.value || '' });
                  if (errors.timeZone) {
                    setErrors(prev => ({ ...prev, timeZone: undefined }));
                  }
                }}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => getTimeZoneDisplayLabel(option?.value || '')}
                groupBy={(option) => option.group}
                autoHighlight
                disablePortal
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('storeRegistration.basicInfo.typeToSearchTimeZone')}
                    error={!!errors.timeZone}
                    helperText={errors.timeZone}
                    sx={getFieldSx(!!errors.timeZone)}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={`${option.group}-${option.value}`}>
                    {getTimeZoneDisplayLabel(option.value)}
                  </Box>
                )}
                slotProps={{
                  paper: {
                    sx: {
                      '& .MuiAutocomplete-groupLabel': {
                        bgcolor: BRAND.background,
                        color: BRAND.accentText,
                        fontWeight: 700,
                      },
                    },
                  },
                }}
                sx={getFieldSx(!!errors.timeZone)}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {t('storeRegistration.basicInfo.slotInterval')}
              </Typography>

              <FormControl fullWidth>
                <Select
                  value={formData.slotInterval || 'Thirty'}
                  onChange={(e) => updateFormData({ slotInterval: e.target.value })}
                  MenuProps={{ disableScrollLock: true }}
                  sx={getSelectSx(false)}
                >
                  <MenuItem value="Five">{t('storeRegistration.basicInfo.slotIntervalFive')}</MenuItem>
                  <MenuItem value="Ten">{t('storeRegistration.basicInfo.slotIntervalTen')}</MenuItem>
                  <MenuItem value="Fifteen">{t('storeRegistration.basicInfo.slotIntervalFifteen')}</MenuItem>
                  <MenuItem value="Thirty">{t('storeRegistration.basicInfo.slotIntervalThirty')}</MenuItem>
                  <MenuItem value="Sixty">{t('storeRegistration.basicInfo.slotIntervalSixty')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

          </Grid>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {formatFieldLabel(t('storeRegistration.basicInfo.supportedLanguages'), !isSelfService)}
            </Typography>

            <LanguageSelector
              languageOptions={languageOptions}
              selectedLanguages={formData.supportedLanguages}
              onLanguageChange={handleLanguageChange}
            />

            {errors.languages && (
              <Alert severity="error" sx={{ mt: 1.5 }}>{errors.languages}</Alert>
            )}
          </Box>

          {shouldShowStoreLanguageField && (
            <Box sx={{ mt: 3, maxWidth: { xs: '100%', md: '50%' } }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {formatFieldLabel(t('storeRegistration.basicInfo.storeLanguage'), !isSelfService)}
              </Typography>

              <FormControl fullWidth>
                <Select
                  value={formData.storeLanguage || ''}
                  onChange={(e) => {
                    updateFormData({ storeLanguage: e.target.value });
                    if (errors.storeLanguage) {
                      setErrors(prev => ({ ...prev, storeLanguage: undefined }));
                    }
                  }}
                  error={!!errors.storeLanguage}
                  MenuProps={{ disableScrollLock: true }}
                  sx={getSelectSx(!!errors.storeLanguage)}
                >
                  {storeLanguageOptions.map((language) => (
                    <MenuItem key={language.code} value={language.code}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LanguageFlag languageCode={language.code} size="1rem" />
                        <span>{language.name}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {errors.storeLanguage && (
                <Alert severity="error" sx={{ mt: 1.5 }}>
                  {errors.storeLanguage}
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* About Field */}
      {formData.supportedLanguages.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {formatFieldLabel(t('storeRegistration.basicInfo.about'), !isSelfService)}
            </Typography>
            
            <MultiLanguageTextArea
              supportedLanguages={formData.supportedLanguages}
              availableLanguages={availableLanguages}
              values={formData.about}
              onChange={handleAboutChange}
              errors={errors.about}
              fieldName="about"
              labelContent="storeRegistration.about"
              multiline={true}
              rows={4}
              idPrefix="about"
              required={!isSelfService}
            />
          </CardContent>
        </Card>
      )}

    </Box>
  );
};

export default StoreBasicInfo;
